import logger from "@/lib/logger";
import { useState, useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useZxing } from "react-zxing";
import axios from "axios";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { StatusBadge, ALL_STATUSES, getStep } from "@/components/StatusPipeline";
import Barcode from "react-barcode";
import { QRCodeSVG } from "qrcode.react";
import {
  ScanLine, Zap, RefreshCw, Eye, CheckCircle, Printer,
  AlertCircle, Loader2, User, Package, CreditCard, Keyboard, X, Plus
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAY_STYLES = {
  "Unpaid": "bg-rose-50 text-rose-700 border-rose-200",
  "Partial": "bg-amber-50 text-amber-700 border-amber-200",
  "Paid":    "bg-emerald-50 text-emerald-700 border-emerald-200",
};

const fmtDate = d => d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "";

export default function Scanner() {
  const navigate = useNavigate();

  const [mode,        setMode]        = useState("scan");  // "scan" | "manual" | "result"
  const [job,         setJob]         = useState(null);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState("");
  const [manualInput, setManualInput] = useState("");
  const [cameraErr,   setCameraErr]   = useState("");
  const [lastScan,    setLastScan]    = useState("");
  const [showStatus,  setShowStatus]  = useState(false);
  const [statusForm,  setStatusForm]  = useState({ status: "", notes: "" });
  const [saving,      setSaving]      = useState(false);
  const cooldown = useRef(false);

  // ---- ZXing scanner ----
  const { ref: videoRef } = useZxing({
    paused: mode !== "scan",
    onDecodeResult(result) {
      const text = result.getText().trim();
      if (!text || cooldown.current || text === lastScan) return;
      cooldown.current = true;
      setLastScan(text);
      lookupJob(text);
      setTimeout(() => { cooldown.current = false; }, 3000);
    },
    onError() {},
    constraints: { video: { facingMode: "environment" } },
  });

  // Handle camera permission error
  useEffect(() => {
    navigator.mediaDevices?.getUserMedia({ video: true })
      .catch(() => setCameraErr("Camera access denied. Use manual entry below."));
  }, []);

  const lookupJob = useCallback(async (query) => {
    setLoading(true); setError(""); setJob(null);
    try {
      const q = query.trim();
      if (q.toUpperCase().startsWith("XSP-")) {
        const invRes = await axios.get(`${API}/inventory/lookup/${q.toUpperCase()}`);
        if (invRes.data.found) {
          const charger = invRes.data.charger;
          const related = invRes.data.related_job;
          if (related) {
            const jobRes = await axios.get(`${API}/jobs/${related.id}`);
            setJob({ ...jobRes.data, _inventoryCharger: charger });
          } else {
            setJob({ _inventoryOnly: true, _charger: charger });
          }
          setMode("result");
          axios.post(`${API}/analytics/event`, { page: "scan" }).catch(() => {});
          setLoading(false);
          return;
        }
      }
      const res = await axios.get(`${API}/jobs/${q}`);
      setJob(res.data);
      setMode("result");
      axios.post(`${API}/analytics/event`, { page: "scan" }).catch(() => {});
    } catch (err) {
      logger.error("[Scanner] lookupJob failed:", err?.message);
      setError(`"${query}" not found. Please check the barcode or job number.`);
      setMode("scan");
    } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // API and axios are stable module-level references — safe to omit

  const handleManualSubmit = (e) => {
    e.preventDefault();
    if (manualInput.trim()) lookupJob(manualInput.trim());
  };

  const updateStatus = async () => {
    if (!statusForm.status || !job) return;
    setSaving(true);
    try {
      const r = await api.put(`${API}/jobs/${job.id}/status`, statusForm);
      setJob(r.data);
      toast.success(`✅ Status updated to "${statusForm.status}"`);
      setShowStatus(false);
    } catch { toast.error("Failed to update status"); }
    finally { setSaving(false); }
  };

  const resetScanner = () => {
    setJob(null); setError(""); setLastScan("");
    setManualInput(""); setMode("scan");
  };

  const printLabel = () => {
    window.open(`/print/${job.id}`, "_blank");
  };

  // ---- RESULT SCREEN ----
  if (mode === "result" && job) {

    // Inventory-only charger (no repair job yet)
    if (job._inventoryOnly) {
      const c = job._charger;
      return (
        <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-2xl mx-auto">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-green-600 flex items-center justify-center shadow"><ScanLine size={20} className="text-white"/></div>
              <div>
                <h1 className="text-lg font-bold text-slate-900" style={{fontFamily:"'Space Grotesk',sans-serif"}}>Charger Found ✅</h1>
                <p className="text-xs text-slate-400">From inventory — no service job yet</p>
              </div>
            </div>
            <Button variant="outline" size="sm" className="h-8 text-xs" onClick={resetScanner}><RefreshCw size={13} className="mr-1.5"/>Scan Another</Button>
          </div>
          <Card className="rounded-2xl border-2 border-green-300 bg-white mb-4">
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <span className="font-mono text-2xl font-extrabold text-green-700">{c.barcode}</span>
                  <p className="text-xs text-green-600 font-semibold mt-0.5">Status: {c.status}</p>
                </div>
                <Barcode value={c.barcode} width={1.2} height={35} fontSize={10} margin={3} background="transparent"/>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[["Spec",c.charger_spec],["Model",c.model],["Body",c.body_type]].map(([l,v])=>(
                  <div key={l}><p className="text-xs text-slate-400">{l}</p><p className="text-sm font-semibold text-slate-900">{v||"—"}</p></div>
                ))}
              </div>
              {c.notes && <p className="text-xs text-slate-500 mt-3">Notes: {c.notes}</p>}
            </CardContent>
          </Card>
          <div className="grid grid-cols-2 gap-3">
            <Button className="bg-green-600 hover:bg-green-700 text-white h-11" onClick={()=>navigate("/jobs/new")}>
              <CheckCircle size={16} className="mr-2"/>Create Service Job
            </Button>
            <Button variant="outline" className="h-11" onClick={resetScanner}><RefreshCw size={16} className="mr-2"/>Scan Another</Button>
          </div>
        </div>
      );
    }

    const due = job.payment?.due_amount || 0;
    return (
      <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-2xl mx-auto">
        {/* Success header */}
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-green-600 flex items-center justify-center shadow">
              <ScanLine size={20} className="text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
                Barcode Scanned!
              </h1>
              <p className="text-xs text-slate-400">Job found successfully</p>
            </div>
          </div>
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={resetScanner}>
            <RefreshCw size={13} className="mr-1.5" /> Scan Another
          </Button>
        </div>

        {/* Job Summary Card */}
        <Card className="rounded-2xl border-2 border-green-300 bg-white mb-4 shadow-sm">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <span className="font-mono text-2xl font-extrabold text-green-700 tracking-tight">{job.job_no}</span>
                <div className="mt-1"><StatusBadge status={job.current_status} size="lg" /></div>
              </div>
              {/* Mini barcode */}
              <div className="text-right">
                <Barcode value={job.job_no} width={1.2} height={35} fontSize={10} margin={3} background="transparent" />
              </div>
            </div>

            {/* Customer */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="flex items-start gap-2">
                <User size={14} className="text-slate-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-slate-400">Customer</p>
                  <p className="text-sm font-semibold text-slate-900">{job.customer?.name}</p>
                  <p className="text-xs text-slate-500">{job.customer?.phone}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <Package size={14} className="text-slate-400 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-xs text-slate-400">Chargers</p>
                  <p className="text-sm font-semibold text-slate-900">{(job.products || []).length} unit{(job.products || []).length !== 1 ? "s" : ""}</p>
                  {(job.products || [])[0]?.charger_spec && (
                    <p className="text-xs font-mono text-green-700">{job.products[0].charger_spec}</p>
                  )}
                </div>
              </div>
            </div>

            {/* All chargers */}
            {(job.products || []).length > 0 && (
              <div className="bg-slate-50 rounded-xl p-3 mb-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Charger Details</p>
                {(job.products || []).map((p, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs py-1 border-b border-slate-100 last:border-0">
                    <span className="w-5 h-5 rounded-full bg-green-100 text-green-700 text-xs font-bold flex items-center justify-center flex-shrink-0">{i+1}</span>
                    <span className="font-mono font-semibold text-green-700">{p.charger_spec || "—"}</span>
                    {p.serial_number && <span className="text-slate-400 font-mono">SN: {p.serial_number}</span>}
                    {p.body_type && <span className="ml-auto text-slate-500">{p.body_type}</span>}
                  </div>
                ))}
              </div>
            )}

            {/* Fault */}
            {job.specs?.fault_description && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
                <p className="text-xs font-semibold text-amber-700 mb-1">Fault Description</p>
                <p className="text-sm text-amber-900">{job.specs.fault_description}</p>
              </div>
            )}

            {/* Payment */}
            <div className="flex items-center justify-between text-xs">
              <div className="flex items-center gap-2">
                <CreditCard size={13} className="text-slate-400" />
                <span className={`px-2 py-0.5 rounded-full border font-medium ${PAY_STYLES[job.payment?.payment_status] || "bg-slate-100 text-slate-600"}`}>
                  {job.payment?.payment_status}
                </span>
                <span className="text-slate-500">₹{(job.payment?.amount || 0).toLocaleString("en-IN")}</span>
                {due > 0 && <span className="text-rose-600 font-semibold">Due: ₹{due.toLocaleString("en-IN")}</span>}
              </div>
              <span className="text-slate-400">Received {fmtDate(job.created_at)}</span>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <Button className="bg-green-600 hover:bg-green-700 text-white h-11 text-sm"
            onClick={() => { setStatusForm({ status: job.current_status, notes: "" }); setShowStatus(true); }}>
            <CheckCircle size={16} className="mr-2" /> Update Status
          </Button>
          <Button variant="outline" className="h-11 text-sm"
            onClick={() => navigate(`/jobs/${job.id}`)}>
            <Eye size={16} className="mr-2" /> View Full Job
          </Button>
          <Button variant="outline" className="h-11 text-sm border-blue-200 text-blue-700 hover:bg-blue-50"
            onClick={printLabel}>
            <Printer size={16} className="mr-2" /> Print Invoice
          </Button>
          <Button variant="outline" className="h-11 text-sm" onClick={resetScanner}>
            <ScanLine size={16} className="mr-2" /> Scan Again
          </Button>
        </div>

        {/* Update Status Dialog */}
        <Dialog open={showStatus} onOpenChange={setShowStatus}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: "'Space Grotesk',sans-serif" }}>Update Status — {job.job_no}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label className="text-xs font-medium">New Status</Label>
                <Select value={statusForm.status} onValueChange={v => setStatusForm({ ...statusForm, status: v })}>
                  <SelectTrigger className="mt-1.5 h-9 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="max-h-64">
                    {ALL_STATUSES.map(s => {
                      const step = getStep(s);
                      return (
                        <SelectItem key={s} value={s}>
                          <span className="flex items-center gap-2"><span>{step?.emoji}</span><span>{s}</span></span>
                        </SelectItem>
                      );
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-medium">Note (optional)</Label>
                <Textarea className="mt-1.5 text-sm" rows={2}
                  placeholder="Add a note..."
                  value={statusForm.notes}
                  onChange={e => setStatusForm({ ...statusForm, notes: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" size="sm" onClick={() => setShowStatus(false)}>Cancel</Button>
              <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white"
                disabled={saving || !statusForm.status} onClick={updateStatus}>
                {saving ? <Loader2 size={13} className="mr-1 animate-spin" /> : null} Update
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  // ---- SCANNER SCREEN ----
  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-xl bg-green-600 flex items-center justify-center">
          <ScanLine size={20} className="text-white" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-slate-900" style={{ fontFamily: "'Space Grotesk',sans-serif" }}>
            Barcode Scanner
          </h1>
          <p className="text-xs text-slate-400">Scan the sticker on any charger to get job details instantly</p>
        </div>
      </div>

      {/* Camera view */}
      <Card className="rounded-2xl border-2 border-green-200 bg-black overflow-hidden mb-4 relative" style={{ minHeight: "280px" }}>
        {cameraErr ? (
          <div className="flex flex-col items-center justify-center min-h-[280px] gap-3 p-6">
            <AlertCircle size={32} className="text-amber-400" />
            <p className="text-amber-300 text-sm text-center">{cameraErr}</p>
            <p className="text-slate-500 text-xs text-center">Use the manual entry below instead</p>
          </div>
        ) : (
          <>
            <video
              ref={videoRef}
              className="w-full object-cover"
              style={{ minHeight: "280px" }}
              autoPlay
              muted
              playsInline
            />
            {/* Scan overlay */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="relative w-64 h-20">
                {/* Corner brackets */}
                <div className="absolute top-0 left-0 w-6 h-6 border-t-3 border-l-3 border-green-400 border-t-[3px] border-l-[3px]" />
                <div className="absolute top-0 right-0 w-6 h-6 border-t-3 border-r-3 border-green-400 border-t-[3px] border-r-[3px]" />
                <div className="absolute bottom-0 left-0 w-6 h-6 border-b-3 border-l-3 border-green-400 border-b-[3px] border-l-[3px]" />
                <div className="absolute bottom-0 right-0 w-6 h-6 border-b-3 border-r-3 border-green-400 border-b-[3px] border-r-[3px]" />
                {/* Scan line animation */}
                <div className="absolute left-1 right-1 h-0.5 bg-green-400 opacity-80 animate-bounce" style={{ top: "50%" }} />
              </div>
            </div>
            {/* Loading indicator */}
            {loading && (
              <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                <div className="text-center">
                  <Loader2 size={32} className="text-green-400 animate-spin mx-auto mb-2" />
                  <p className="text-white text-sm">Looking up job...</p>
                </div>
              </div>
            )}
            <div className="absolute bottom-3 left-0 right-0 text-center">
              <p className="text-white/80 text-xs bg-black/40 inline-block px-3 py-1 rounded-full">
                Point camera at barcode on the charger sticker
              </p>
            </div>
          </>
        )}
      </Card>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-rose-50 border border-rose-200 rounded-xl mb-4 text-sm text-rose-700">
          <AlertCircle size={15} /> {error}
          <button onClick={() => setError("")} className="ml-auto"><X size={14} /></button>
        </div>
      )}

      {/* Manual Entry */}
      <Card className="rounded-2xl border border-slate-200/70 bg-white">
        <CardContent className="p-5">
          <div className="flex items-center gap-2 mb-3">
            <Keyboard size={15} className="text-slate-500" />
            <p className="text-sm font-semibold text-slate-700">Manual Entry</p>
            <span className="text-xs text-slate-400">— type job number if camera not working</span>
          </div>
          <form onSubmit={handleManualSubmit} className="flex gap-2">
            <Input
              placeholder="e.g. XP-0001"
              value={manualInput}
              onChange={e => setManualInput(e.target.value.toUpperCase())}
              className="h-9 text-sm font-mono flex-1"
              data-testid="scanner-manual-input"
            />
            <Button type="submit" className="bg-green-600 hover:bg-green-700 text-white h-9 px-4 text-sm"
              disabled={loading || !manualInput.trim()}
              data-testid="scanner-manual-submit">
              {loading ? <Loader2 size={14} className="animate-spin" /> : "Go"}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* How to use */}
      <div className="mt-5 p-4 bg-green-50 rounded-2xl border border-green-200">
        <p className="text-xs font-semibold text-green-700 mb-2">How to use the barcode sticker</p>
        <div className="space-y-1.5 text-xs text-green-800">
          <p>1️⃣ Open Job Detail for any job → Print Invoice</p>
          <p>2️⃣ Cut out the barcode from the printed invoice</p>
          <p>3️⃣ Stick it on the charger using tape or adhesive label</p>
          <p>4️⃣ Next time — just scan to pull up all job details instantly!</p>
        </div>
      </div>
    </div>
  );
}
